import { SourceRail, type SourceRailItem, type SourceRailVariant } from "@ando/ui";
import { Trans } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";

import { selectMessageById, useChatStore } from "@/features/chat/state/chatStore";
import {
  type WorkspaceTab,
  selectCurrentTab,
  useWorkspaceStore,
} from "@/features/workspace-router/state/workspaceStore";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import { computeRelevance } from "./relevance";
import { WORKSPACE_TAB_LABEL, destinationForSource } from "./sourceDestination";

/**
 * SourceRailSurface — single mount point for the in-tab source picker.
 *
 * Subscribes to ONE specific message (via ``selectMessageById``) so the
 * surface doesn't re-render on every WS token during streaming — the
 * naive ``selectMessages`` selector would rebuild the array reference on
 * each chunk and cause a render storm in long conversations.
 *
 * Renders nothing when:
 *   - no ``messageId`` is provided (the user navigated manually without
 *     auto-open context — show the tab's normal list view);
 *   - the message has fewer than 2 sources (single chip is visual debt).
 *
 * Click on a chip → ``navigate({ to: <destinationTab>, search: { focus,
 * msg } })``. Cross-type chips swap tab automatically because the route
 * differs — the user sees the tab pulse (Ando-routing animation) so the
 * lateral jump is signposted.
 *
 * Mount once per hub tab at the TOP of the surface, ABOVE the inline
 * viewer. See ``documents.tsx`` / ``conversations.tsx`` / ``calendar.tsx``
 * for call-sites — all three use this same surface DRY.
 */
export interface SourceRailSurfaceProps {
  /**
   * The ``?msg=<id>`` search param value — the chat message id whose
   * sources populate the rail. ``null`` when the user opened the tab
   * manually (no auto-open context) → surface renders nothing.
   */
  readonly messageId: string | null;
  /**
   * The currently-active focus id (from ``?focus=`` search param).
   * Used to style the active chip. ``null`` when the tab is in list view.
   */
  readonly activeFocusId: string | null;
  /**
   * Visual variant. Defaults to ``"default"`` (workspace-hub rich rail
   * — ``FocusedViewLayout`` use-case).
   *
   * Pass ``"footnote"`` for the in-chat citation strip mounted UNDER
   * each agent bubble (June 14, 2026 — user feedback: actionable
   * chips like "Grafico" were getting drowned out by neighbouring
   * source citation chips of equal visual weight). The footnote
   * variant prepends a small "Fonti" micro-label, wraps the rail in
   * a ``<nav>`` landmark, and demotes each chip to text-only chrome.
   */
  readonly variant?: SourceRailVariant;
}

export const SourceRailSurface: React.FC<SourceRailSurfaceProps> = ({
  messageId,
  activeFocusId,
  variant = "default",
}) => {
  // Pin-point subscription — re-renders only when THIS message mutates,
  // not on every WS token of the active conversation.
  const message = useChatStore(selectMessageById(messageId));
  const currentTab = useWorkspaceStore(selectCurrentTab);
  const navigate = useNavigate();

  const items = React.useMemo<SourceRailItem[]>(() => {
    if (!message?.sources) return [];
    const result: SourceRailItem[] = [];
    // De-dup by focus id — the BE sometimes ships the same drive doc as
    // a separate ``sources[]`` entry per chunk (May 23, 2026 incident:
    // 6 chips identici sullo stesso .docx). Keep the FIRST entry so
    // the relevance ranking from the BE wins.
    const seen = new Set<string>();
    for (const src of message.sources) {
      const dest = destinationForSource(src);
      if (dest === null) continue;
      if (seen.has(dest.focus)) continue;
      seen.add(dest.focus);
      // Pick a non-empty title — ``??`` only catches null/undefined,
      // but the BE legitimately ships ``subject: ""`` on parser misses.
      // Strip + falsy-check each candidate so the placeholder fires for
      // empty strings too.
      //
      // For chat sources the BE doesn't ship ``subject`` (there's no
      // such concept in a Teams/Slack/GChat message) — fall back to
      // the channel/space name + sender so the chip title reads
      // meaningfully instead of "Senza titolo".
      const chatTitle = (() => {
        if (dest.kind !== "chat") return null;
        const room = src.channel_name?.trim() || src.space_name?.trim() || null;
        if (room && src.sender_name?.trim()) {
          return `${src.sender_name.trim()} in ${room.startsWith("#") ? room : `#${room}`}`;
        }
        if (room) return room.startsWith("#") ? room : `#${room}`;
        if (src.sender_name?.trim()) return src.sender_name.trim();
        return null;
      })();
      const candidates = [chatTitle, src.subject, src.source_file];
      let title = "Senza titolo";
      for (const c of candidates) {
        if (typeof c === "string" && c.trim().length > 0) {
          title = c.trim();
          break;
        }
      }
      result.push({
        id: dest.focus,
        kind: dest.kind,
        title,
        // Pick the most informative score field — different specialists
        // populate different fields (web → `relevance_score`, docs/email
        // → `rerank_score` or `final_score`). Without this fallback the
        // chip tooltip read "0% RILEVANZA" for every internal source
        // because the wire only set `relevance_score` on web (May 24,
        // 2026 — explicit user feedback).
        relevance: computeRelevance(src),
        destinationTab: dest.tabLabel,
        // Jul 26, 2026 (#987) — `??` only skips null/undefined, but the BE
        // sends "" (empty string, not null) for content on non-web sources,
        // so it always short-circuited here before ever reaching the real
        // excerpt in email_snippet/text. Same empty-string-aware pattern as
        // the `title` fallback above.
        snippet: [src.content, src.email_snippet, src.text].find(
          (s): s is string => typeof s === "string" && s.trim().length > 0,
        ),
        timestamp: src.sent_at ?? src.modified_date ?? src.published_date,
        // Web sources carry the external URL — drives the favicon +
        // domain badge in the hover popover. Non-web kinds drop this.
        url: dest.kind === "web" ? src.url : undefined,
        // Email sources project their wire fields into a compact meta
        // bundle for the popover header / meta strip. Only the fields
        // the UI displays are forwarded — full sender list + body live
        // in the focused viewer.
        email:
          dest.kind === "email"
            ? {
                senderName: src.sender_name,
                sender: src.sender,
                to: src.recipients?.[0],
                sentAt: src.sent_at,
                attachmentsCount: src.attachment_names?.length,
              }
            : undefined,
        // Chat sources (Teams / Slack / Google Chat) project provider
        // + breadcrumb fields for the popover. Provider key matches
        // the BE wire (lowercased downstream by the header).
        chat:
          dest.kind === "chat"
            ? {
                provider: src.provider,
                teamName: src.team_name,
                channelName: src.channel_name,
                spaceName: src.space_name,
                senderName: src.sender_name,
                participantNames: src.participant_names,
                lastMessageAt: src.last_message_at ?? src.sent_at,
              }
            : undefined,
      });
    }
    return result;
  }, [message]);

  const handleSelect = React.useCallback(
    (item: SourceRailItem) => {
      // Re-derive the destination tab from the original source via the
      // single-source-of-truth mapper — ``item.destinationTab`` carries
      // the label (used in the chip tooltip), not the tab slug. Looking
      // it up here keeps the click handler honest against any future
      // ``destinationForSource`` change.
      if (!message) return;
      const src = message.sources?.find((s) => s.doc_id === item.id);
      if (!src) return;
      const dest = destinationForSource(src);
      if (dest === null) return;
      const def = getWorkspaceTabDef(dest.tab);
      if (!def) return;
      void navigate({
        to: def.route,
        search: { focus: dest.focus, msg: messageId ?? undefined },
      });
    },
    [message, messageId, navigate],
  );

  // Render with ≥1 source — the rail is the user's only way back to the
  // citations of the originating chat answer once they navigate away
  // from the focused viewer (May 24, 2026: dropped the previous "< 2"
  // visual-debt threshold per explicit user feedback "non posso più
  // rivedere le sources").
  if (items.length < 1) {
    // Sources land with the FINAL answer frame (not during token streaming),
    // so a streaming agent bubble has none yet. In the in-chat footnote strip
    // — where users notice the gap most — show a skeleton instead of nothing,
    // so the absent citations read as "loading", not "missing" or "broken".
    if (variant === "footnote" && message?.status === "streaming") {
      return (
        <nav aria-label="Fonti citate" aria-busy className="flex flex-col">
          <span className="mb-1.5 block font-medium text-[10px] text-ink-3 uppercase tracking-[0.08em]">
            <Trans>Fonti</Trans>
          </span>
          <div className="flex items-center gap-1.5">
            <span className="h-6 w-32 animate-pulse rounded-md bg-ink-3/10" />
            <span className="h-6 w-24 animate-pulse rounded-md bg-ink-3/10" />
            <span className="text-[11px] text-ink-3">
              <Trans>recupero fonti…</Trans>
            </span>
          </div>
        </nav>
      );
    }
    return null;
  }

  const currentTabLabel = WORKSPACE_TAB_LABEL[currentTab as WorkspaceTab] ?? currentTab;

  // Footnote variant — wrap the rail in a ``<nav>`` landmark with a
  // micro "Fonti" eyebrow above so the row reads as a citation
  // footnote, not a row of equal-weight pills. The label fires for
  // ≥1 source (matches the relaxed render threshold above).
  if (variant === "footnote") {
    return (
      <nav aria-label="Fonti citate" className="flex flex-col">
        <span className="mb-1.5 block font-medium text-[10px] text-ink-3 uppercase tracking-[0.08em]">
          <Trans>Fonti</Trans>
        </span>
        <SourceRail
          items={items}
          activeId={activeFocusId}
          currentTab={currentTabLabel}
          onSelect={handleSelect}
          variant="footnote"
        />
      </nav>
    );
  }

  return (
    <SourceRail
      items={items}
      activeId={activeFocusId}
      currentTab={currentTabLabel}
      onSelect={handleSelect}
    />
  );
};
SourceRailSurface.displayName = "SourceRailSurface";
